import React from 'react';

export const CustomAlert = ({ 
  isOpen, 
  onClose, 
  type, 
  title, 
  message, 
  details, 
  onConfirm, 
  showConfirmButton = false, 
  confirmText = 'Confirm', 
  cancelText = 'Cancel' 
}) => {
  if (!isOpen) return null;

  const getAlertStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: '✓',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          titleColor: 'text-green-800',
          messageColor: 'text-green-700',
          buttonBg: 'bg-green-600 hover:bg-green-700'
        };
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: '✕',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          titleColor: 'text-red-800',
          messageColor: 'text-red-700',
          buttonBg: 'bg-red-600 hover:bg-red-700'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: '⚠',
          iconBg: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          titleColor: 'text-yellow-800',
          messageColor: 'text-yellow-700',
          buttonBg: 'bg-yellow-600 hover:bg-yellow-700'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'ℹ',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          titleColor: 'text-blue-800',
          messageColor: 'text-blue-700',
          buttonBg: 'bg-blue-600 hover:bg-blue-700'
        };
    }
  };

  const styles = getAlertStyles();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${styles.bg} ${styles.border} border-2 rounded-lg shadow-xl max-w-md w-full mx-4 animate-fade-in`}>
        <div className="p-6">
          {/* Header with icon */}
          <div className="flex items-center mb-4">
            <div className={`${styles.iconBg} rounded-full w-10 h-10 flex items-center justify-center mr-3 animate-bounce`}>
              <span className={`${styles.iconColor} text-xl font-bold`}>{styles.icon}</span>
            </div>
            <h3 className={`${styles.titleColor} text-lg font-semibold`}>{title}</h3>
          </div>

          {/* Message */}
          <div className={`${styles.messageColor} text-sm mb-4 leading-relaxed`}>
            {message}
          </div>

          {/* Details (only show for error type) */}
          {details && type === 'error' && (
            <div className="bg-white bg-opacity-60 border border-gray-200 rounded p-3 mb-4">
              <p className="text-xs text-gray-600 font-medium mb-1">Technical Details:</p>
              <p className="text-xs text-gray-700 whitespace-pre-line">{details}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end space-x-3">
            {showConfirmButton ? (
              <>
                {/* Cancel button */}
                <button
                  onClick={onClose}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105"
                >
                  {cancelText}
                </button>
                {/* Confirm button */}
                <button
                  onClick={onConfirm}
                  className={`${styles.buttonBg} text-white px-6 py-2 rounded font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105`}
                >
                  {confirmText}
                </button>
              </>
            ) : (
              /* Single OK button */
              <button
                onClick={onClose}
                className={`${styles.buttonBg} text-white px-6 py-2 rounded font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105`}
              >
                OK
              </button>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CustomAlert;